import React from 'react';
import { Link } from 'react-router-dom';
import { blogPosts } from '../data/blogPosts';

const Resources: React.FC = () => {
  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Exam Tips':
        return 'bg-blue-100 text-blue-800';
      case 'Study Guide':
        return 'bg-green-100 text-green-800';
      case 'Career Advice':
        return 'bg-purple-100 text-purple-800';
      case 'Licensing Guide':
        return 'bg-orange-100 text-orange-800';
      case 'Study Tips':
        return 'bg-indigo-100 text-indigo-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
          <div className="flex justify-between items-center py-3 sm:py-4">
            <Link to="/" className="flex items-center space-x-2 sm:space-x-3 hover:opacity-80 transition-opacity cursor-pointer">
              <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xs sm:text-sm">R</span>
              </div>
              <span className="text-lg sm:text-xl font-bold text-gray-900">RealEstart.io</span>
            </Link>
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <Link to="/#features" className="text-gray-600 hover:text-[#FF6B6B] transition-colors">Features</Link>
              <Link to="/#how-it-works" className="text-gray-600 hover:text-[#FF6B6B] transition-colors">How It Works</Link>
              <Link to="/#testimonials" className="text-gray-600 hover:text-[#FF6B6B] transition-colors">Reviews</Link>
              <span className="text-[#FF6B6B] font-semibold">Resources</span>
              <Link to="/auth" className="text-gray-600 hover:text-[#FF6B6B] transition-colors">Sign In</Link>
              <Link to="/demo" className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2 rounded-full hover:from-blue-700 hover:to-purple-700 transition-all transform hover:scale-105 shadow-lg">Try Demo</Link>
            </div>
            
            {/* Mobile Navigation - Always show sign-in and demo buttons */}
            <div className="flex md:hidden items-center space-x-2">
              <Link to="/auth" className="text-gray-600 hover:text-[#FF6B6B] transition-colors text-sm px-3 py-2">Sign In</Link>
              <Link to="/demo" className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-full hover:from-blue-700 hover:to-purple-700 transition-all transform hover:scale-105 shadow-lg text-sm">Try Demo</Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#FF6B6B] to-[#FF8E53] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-6">Resources & Insights</h1>
            <p className="text-xl mb-8 text-white/90 max-w-3xl mx-auto">
              Expert tips, study guides, and career advice to help you succeed in your real estate journey. 
              Everything you need to pass your Georgia real estate exam and launch your career.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/demo" 
                className="bg-white text-[#FF6B6B] px-6 py-3 rounded-full font-semibold hover:shadow-lg transition-all transform hover:scale-105"
              >
                Try Our Demo
              </Link>
              <Link 
                to="/checkout?plan=trial" 
                className="border-2 border-white text-white px-6 py-3 rounded-full font-semibold hover:bg-white hover:text-[#FF6B6B] transition-all"
              >
                Start Free Trial
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-gray-900">
              Latest Articles
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover proven strategies, study tips, and career guidance from real estate experts.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.map((post) => (
              <article 
                key={post.id}
                className="rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 bg-white border border-gray-200"
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getCategoryColor(post.category)}`}>
                      {post.category}
                    </span>
                    <span className="text-sm text-gray-500">
                      {post.readTime}
                    </span>
                  </div>
                  
                  <h3 className="text-xl font-bold mb-3 line-clamp-2 text-gray-900">
                    {post.title}
                  </h3>
                  
                  <p className="text-sm mb-4 line-clamp-3 text-gray-600">
                    {post.excerpt}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center bg-gray-100">
                        <span className="text-sm font-semibold text-gray-700">
                          {post.author.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {post.author}
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(post.date).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric', 
                            year: 'numeric' 
                          })}
                        </p>
                      </div>
                    </div>
                    
                    <Link
                      to={post.link}
                      className="px-4 py-2 rounded-lg font-semibold text-sm transition-all bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      Read Full Article
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-6">Ready to Ace Your Exam?</h2>
          <p className="text-xl mb-8 text-white/90">
            Join thousands of students who are already using RealEstart to prepare for their real estate exam. 
            Start your free study session today!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/demo" 
              className="bg-white text-blue-600 px-8 py-4 rounded-full font-semibold hover:shadow-lg transition-all transform hover:scale-105"
            >
              Try Demo - No Registration Required!
            </Link>
            <Link 
              to="/checkout?plan=trial"
              className="border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white hover:text-blue-600 transition-all transform hover:scale-105"
            >
              Start Free Trial
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="text-2xl font-bold mb-4">🏠 RealEstart.io</div>
              <p className="text-gray-400">
                Your trusted companion for real estate education and licensing preparation.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/" className="hover:text-white transition-colors">Features</Link></li>
                <li><Link to="/" className="hover:text-white transition-colors">How It Works</Link></li>
                <li><Link to="/" className="hover:text-white transition-colors">Reviews</Link></li>
                <li><Link to="/resources" className="hover:text-white transition-colors">Resources</Link></li>
                <li><Link to="/demo" className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all transform hover:scale-105 shadow-md">Try Demo</Link></li>
                <li><Link to="/auth" className="hover:text-white transition-colors">Sign In</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/help" className="hover:text-white transition-colors">Help Center</Link></li>
                <li><Link to="/contact" className="hover:text-white transition-colors">Contact Us</Link></li>
                <li><button className="hover:text-white transition-colors text-left">Feedback</button></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link></li>
                <li><Link to="/disclaimer" className="hover:text-white transition-colors">Disclaimer</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 RealEstart.io. This is a study aid and is not affiliated with any official exam provider.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Resources;
